import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import CatalogItemGrid from "../components/Products/CatalogItemGrid.jsx";
import PrintLabelGrid from "../components/PrintLabel/PrintLabelGrid.jsx";
import TabNavigation from "../components/TabNavigation.jsx";

/** Workflow tabs filter by status; Catch All has no status filter so users can find any item. */
const CATALOG_TABS = [
    { label: "Awaiting Pickup", value: 0, status: "Awaiting Pickup" },
    { label: "Needs Label", value: 1 },
    { label: "To Inspect", value: 2, status: "Inspection" },
    { label: "Putaway", value: 3, status: "Putaway" },
    { label: "Listed", value: 4, status: "Listed" },
    { label: "Missing", value: 5, status: "Missing" },
    { label: "Catch All", value: 6 },
];

const NEEDS_LABEL_TAB = 1;

function preserveNavParams(searchParams) {
    const next = {};
    const q = searchParams.get("q");
    const gf = searchParams.get("gf");
    const fromInvoice = searchParams.get("fromInvoice");
    const fromShipment = searchParams.get("fromShipment");
    if (q) next.q = q;
    if (gf) next.gf = gf;
    if (fromInvoice) next.fromInvoice = fromInvoice;
    if (fromShipment) next.fromShipment = fromShipment;
    return next;
}

export default function CatalogItems() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [value, setValue] = useState(() => {
        const tabFromUrl = parseInt(searchParams.get("t"), 10);
        return CATALOG_TABS.some((tab) => tab.value === tabFromUrl) ? tabFromUrl : 0;
    });

    const handleChange = (_event, newValue) => {
        setValue(newValue);
        setSearchParams({
            ...preserveNavParams(searchParams),
            t: String(newValue),
        });
    };

    useEffect(() => {
        const raw = searchParams.get("t");
        const tabFromUrl = parseInt(raw, 10);
        const valid =
            !Number.isNaN(tabFromUrl) &&
            CATALOG_TABS.some((tab) => tab.value === tabFromUrl);

        if (valid) {
            if (tabFromUrl !== value) setValue(tabFromUrl);
            return;
        }
        if (value !== 0) setValue(0);
        if (raw !== "0") {
            setSearchParams(
                { ...preserveNavParams(searchParams), t: "0" },
                { replace: true }
            );
        }
    }, [searchParams, setSearchParams, value]);

    const activeTab = useMemo(
        () => CATALOG_TABS.find((tab) => tab.value === value) ?? CATALOG_TABS[0],
        [value]
    );

    const fromInvoice = searchParams.get("fromInvoice") || "";
    const fromShipment = searchParams.get("fromShipment") || "";
    const urlQ = searchParams.get("q") || "";

    return (
        <CatalogPageShell
            title="Catalog Items"
            subtitle="Track items by workflow stage — pickup, labeling, inspection, putaway, and listing."
        >
            <div className="app-page-stack">
                <div style={{ flexShrink: 0 }}>
                    <TabNavigation
                        value={value}
                        onChange={handleChange}
                        tabs={CATALOG_TABS}
                    />
                </div>
                {value === NEEDS_LABEL_TAB ? (
                    <PrintLabelGrid initialSearch={urlQ} />
                ) : (
                    <CatalogItemGrid
                        // Remount on tab change only — including urlQ remounted on every
                        // keystroke after debounce and stole focus from the search field.
                        key={activeTab.status ?? "catch-all"}
                        statusFilter={activeTab.status}
                        statusNotIn={activeTab.statusNotIn}
                        defaultStatus={activeTab.status ?? ""}
                        initialSearch={urlQ}
                        fromInvoiceId={fromInvoice}
                        fromShipmentId={fromShipment}
                    />
                )}
            </div>
        </CatalogPageShell>
    );
}
